import React, { useCallback, useEffect, useMemo } from 'react';
import { Trans } from 'react-i18next';
import { Image, Stack } from '@fluentui/react';
import clsx from 'clsx';
import { pageDataService } from 'common';
import { ActionButton, PrimaryButton } from 'common/components/Buttons/v8';
import { Card } from 'common/components/Cards/Card';
import { Link } from 'common/components/Link';
import { Heading1, Text } from 'common/components/Text';
import { isWindowsUserAgent } from 'common/hooks/usePlatform';
import { usePromise } from 'common/hooks/usePromise';
import { useScreen } from 'common/hooks/useScreen';
import { ImageConstants } from 'common/ImageConstants';
import { getAssetPath } from 'common/utilities/AssetsPath';
import { getQueryStringParameter } from 'common/utilities/QueryStringHelper';
import TelemetryProvider from 'common/utilities/TelemetryProvider';
import { Configuration } from 'Configuration';
import { useLocalizedStrings } from 'i18n';
import { useAssetPath } from 'store/hooks';

import { stackTokens, useAppLauncherStyles } from './AppLauncherPage.styles';
import { launchProtocolUrl } from './LauncherHelpers';
import { sanitizeRedirectUrl } from './redirectUrlValidator';

const AppLauncherPage = () => {
  const styles = useAppLauncherStyles();
  const { t } = useLocalizedStrings();
  const scopePromise = usePromise();

  const defenderLogo = getAssetPath(ImageConstants.DefenderShield);
  const deviceShield = useAssetPath(ImageConstants.DeviceShieldPng);

  const isWindowsOS = useMemo(() => isWindowsUserAgent(), []);
  const { isMobileScreen } = useScreen();

  const redirectRelativeUrl = useMemo(() => {
    const paramValue: string = getQueryStringParameter('redirectUrl') || '';
    let decodedValue = decodeURIComponent(paramValue).trim();

    // remove any leading '/' character
    if (decodedValue.startsWith('/')) {
      decodedValue = decodedValue.substring(1);
    }

    // Validate the redirect URL to prevent open redirect attacks (CodeQL SM01507)
    return sanitizeRedirectUrl(decodedValue, '');
  }, []);

  const launchInApp = useCallback(() => {
    const defenderUrl = `msdefender://${redirectRelativeUrl}`;
    return scopePromise(launchProtocolUrl(defenderUrl)).catch(() => {
      /*
       App failed to launch, either because it was not installed, or user didnt allow the browser to open the app within the timeout.
       Unfortunately there is no deterministic way to identify the exact reason why it happen. But its good to log it anyways.
       */

      TelemetryProvider.Instance.trackEvent('Launcher_AppFailedToLaunch');
    });
  }, [redirectRelativeUrl, scopePromise]);

  useEffect(() => {
    if (isWindowsOS) {
      launchInApp();
    } else {
      // TEMP: for non windows OS, redirect to the webpage directly
      window.location.href = `/${redirectRelativeUrl}`;
    }
  }, [isWindowsOS, launchInApp, redirectRelativeUrl]);

  if (!isWindowsOS) {
    return null;
  }

  return (
    <Stack
      verticalAlign="center"
      verticalFill
      horizontalAlign="center"
      tokens={stackTokens.pageWrapper}
      className={styles.pageWrapper}
    >
      <Card className={styles.card} maxWidth={526}>
        <Stack tokens={stackTokens.cardContentWrapper}>
          <Stack horizontal verticalAlign="center" tokens={stackTokens.logoHeading} className={styles.logoHeading}>
            <Stack.Item>
              <Image alt="" width={24} src={defenderLogo} />
            </Stack.Item>
            <Stack.Item className={styles.productNameWrapper}>
              <Text variant="Body2">{Configuration.FullProductName}</Text>
            </Stack.Item>
          </Stack>

          <Stack.Item align="center">
            <Image alt="" width={256} src={deviceShield} />
          </Stack.Item>

          <Stack tokens={stackTokens.textContainer}>
            <Stack.Item className={styles.text}>
              <Heading1 variant="Title1">
                <Trans
                  t={t}
                  i18nKey="AppLauncher.Card.Title"
                  components={{
                    branded: <Text variant="Title1" className={styles.brandedText} />,
                  }}
                />
              </Heading1>
            </Stack.Item>
            <Stack.Item className={styles.text}>
              <Text variant="Body1">{t('AppLauncher.Card.Subtitle')}</Text>
            </Stack.Item>
          </Stack>

          <Stack
            horizontal={!isMobileScreen}
            verticalAlign="center"
            horizontalAlign="center"
            tokens={stackTokens.buttonSection}
            className={styles.buttonSection}
          >
            <PrimaryButton
              href={pageDataService.getDownloadLinks().link}
              telemetryEventName="Launcher_DownloadApp"
              className={clsx(styles.button, styles.primaryButton)}
            >
              {t('AppLauncher.Card.Button.GetApp')}
            </PrimaryButton>
            <ActionButton href={`/${redirectRelativeUrl}`} telemetryEventName="Launcher_ContinueOnBrowser" className={styles.button}>
              {t('AppLauncher.Card.Button.ContinueOnBrowser')}
            </ActionButton>
          </Stack>

          <Stack horizontalAlign="center" verticalAlign="center">
            <Text variant="Body2">
              <Trans
                t={t}
                i18nKey="AppLauncher.Card.OpenApp"
                components={{
                  openbtn: <Link onClick={launchInApp} telemetryEventName="Launcher_OpenApp" />,
                }}
              />
            </Text>
          </Stack>
        </Stack>
      </Card>
    </Stack>
  );
};

export default AppLauncherPage;
